import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { GridPaginationState } from "@mui/x-data-grid/hooks/features/pagination/gridPaginationInterfaces";
import { OmsOrder, OrderStatus, FulfillmentStatus } from "../../model/Oms";
import { orderShortRef } from "../../helpers/orderRef";

interface IProps {
  orders: OmsOrder[];
  page: number;
  perPage?: number;
  setPerPage?: (perPage: number) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  totalPages: number;
  totalResults?: number;
  loading: boolean;
  handlePageChange: (newPage: number) => void;
  openOrderDetail: (order: OmsOrder) => void;
}

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_OFFER: "Pendente Proposta",
  PAYMENT_PENDING: "Pag. Pendente",
  PAID: "Pago",
  FULFILLMENT_INIT: "A Processar",
  PAID_PENDING_FULFILLMENT: "Pago — Ful. Pendente",
  COMPLETED: "Concluído",
  REFUNDED: "Reembolsado",
  FAILED: "Falhado",
  CANCELLED: "Cancelado",
  OFFER_SENT: "Proposta Enviada",
};

const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING_OFFER: "bg-yellow-100 text-yellow-800",
  PAYMENT_PENDING: "bg-orange-100 text-orange-800",
  PAID: "bg-blue-100 text-blue-800",
  FULFILLMENT_INIT: "bg-indigo-100 text-indigo-800",
  PAID_PENDING_FULFILLMENT: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-green-100 text-green-800",
  REFUNDED: "bg-gray-200 text-gray-700",
  FAILED: "bg-red-100 text-red-800",
  CANCELLED: "bg-red-50 text-red-700",
  OFFER_SENT: "bg-cyan-100 text-cyan-800",
};

const FULFILLMENT_STATUS_LABELS: Record<FulfillmentStatus, string> = {
  PENDING: "Pendente",
  CREATED: "Criado",
  ACCEPTED: "Aceite",
  CONFIRMED: "Confirmado",
  CANCELLED: "Cancelado",
  FAILED: "Falhado",
};

const FULFILLMENT_STATUS_COLORS: Record<FulfillmentStatus, string> = {
  PENDING: "bg-gray-100 text-gray-700",
  CREATED: "bg-blue-100 text-blue-700",
  ACCEPTED: "bg-indigo-100 text-indigo-700",
  CONFIRMED: "bg-green-100 text-green-700",
  CANCELLED: "bg-yellow-100 text-yellow-800",
  FAILED: "bg-red-100 text-red-800",
};

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: currency || "EUR",
  }).format(cents / 100);
}

const StatusBadge: React.FC<{ label: string; colorClass: string }> = ({
  label,
  colorClass,
}) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}
  >
    {label}
  </span>
);

const ListOmsOrderItems: React.FC<IProps> = ({
  orders,
  page,
  perPage,
  setPerPage,
  totalPages,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  handlePageChange,
  openOrderDetail,
  totalResults,
  loading,
}) => {
  const [showListedFields, setShowListedFields] = React.useState<
    Map<string, boolean>
  >(() => {
    const session = sessionStorage.getItem("oms_orders_listed_fields");
    if (session) {
      return new Map<string, boolean>(JSON.parse(session));
    }
    return new Map<string, boolean>([
      ["id", true],
      ["customer_name", true],
      ["customer_email", true],
      ["payment_method", true],
      ["total_cents", true],
      ["status", true],
      ["aggregate_status", true],
      ["idempotency_key", true],
      ["created_at", true],
      ["updated_at", false],
    ]);
  });

  // Save showListedFields to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem(
      "oms_orders_listed_fields",
      JSON.stringify(Array.from(showListedFields.entries())),
    );
  }, [showListedFields]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Ref.",
      width: 130,
      sortable: true,
      valueGetter: (_value, row) => orderShortRef(row.id),
      renderCell: (params) => (
        <span className="font-mono text-xs text-gray-500">{params.value}</span>
      ),
    },
    {
      field: "idempotency_key",
      headerName: "Chave idempot.",
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => row.idempotency_key || "—",
      renderCell: (params) => (
        <span className="font-mono text-xs">{params.value}</span>
      ),
    },
    {
      field: "customer_name",
      headerName: "Cliente",
      width: 180,
      sortable: true,
      valueGetter: (_value, row) => row.customer_name || "—",
    },
    {
      field: "customer_email",
      headerName: "Email",
      width: 200,
      sortable: true,
      valueGetter: (_value, row) => row.customer_email || "—",
    },
    {
      field: "payment_method",
      headerName: "Pagamento",
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => row.payment_method || "—",
    },
    {
      field: "total_cents",
      headerName: "Total",
      width: 110,
      sortable: true,
      type: "number",
      valueGetter: (_value, row) =>
        formatCents(row.total_cents ?? 0, row.currency),
    },
    {
      field: "status",
      headerName: "Estado",
      width: 140,
      sortable: true,
      renderCell: (params) => {
        const status = params.row.status as OrderStatus;
        return (
          <StatusBadge
            label={ORDER_STATUS_LABELS[status] || status}
            colorClass={
              ORDER_STATUS_COLORS[status] || "bg-gray-100 text-gray-700"
            }
          />
        );
      },
    },
    {
      field: "aggregate_status",
      headerName: "Fulfillment",
      width: 130,
      sortable: true,
      renderCell: (params) => {
        const status = params.row.aggregate_status as FulfillmentStatus;
        return (
          <StatusBadge
            label={FULFILLMENT_STATUS_LABELS[status] || status}
            colorClass={
              FULFILLMENT_STATUS_COLORS[status] || "bg-gray-100 text-gray-700"
            }
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Criada em",
      width: 140,
      sortable: true,
      valueGetter: (_value, row) =>
        row?.created_at
          ? new Date(row.created_at).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      field: "updated_at",
      headerName: "Alterada em",
      width: 140,
      sortable: true,
      valueGetter: (_value, row) =>
        row?.updated_at
          ? new Date(row.updated_at).toLocaleString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
  ];

  const paginationState: GridPaginationState = {
    paginationMode: "server",
    paginationModel: {
      page: page - 1,
      pageSize: perPage || 20,
    },
    rowCount: totalResults || -1,
    enabled: true,
    meta: {
      hasNextPage: true,
    },
  };

  const handlePaginationChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    if (model.pageSize !== perPage && setPerPage) {
      setPerPage(model.pageSize);
    }
    if (model.page + 1 !== page) {
      handlePageChange(model.page + 1);
    }
  };

  return (
    <Paper sx={{ width: "100%" }}>
      <DataGrid
        rows={orders}
        columns={columns}
        loading={loading}
        density="standard"
        initialState={{ pagination: paginationState }}
        rowCount={totalResults || 0}
        paginationMode="server"
        sortingMode="server"
        disableColumnFilter={true}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={(model) => {
          if (model.length > 0) {
            const sortModel = model[0];
            setSortBy(sortModel.field);
            setSortOrder(sortModel.sort || "asc");
          }
        }}
        onRowClick={(params) => openOrderDetail(params.row)}
        columnVisibilityModel={Object.fromEntries(showListedFields)}
        onColumnVisibilityModelChange={(newModel) => {
          const newMap = new Map<string, boolean>(Object.entries(newModel));
          setShowListedFields(newMap);
        }}
        hideFooterSelectedRowCount={true}
        pageSizeOptions={[10, 20, 50, 100]}
        localeText={{
          columnsManagementSearchTitle: "Procurar",
          columnsManagementNoColumns: "Sem colunas",
          columnsManagementShowHideAllText: "Mostrar/Ocultar Todas",
          columnsManagementReset: "Repor",
          columnsManagementDeleteIconLabel: "Limpar",
          columnMenuLabel: "Menu",
          columnMenuAriaLabel: (columnName: string) =>
            `Menu da coluna ${columnName}`,
          columnMenuShowColumns: "Mostrar colunas",
          columnMenuManageColumns: "Gerir colunas",
          columnMenuFilter: "Filtrar",
          columnMenuHideColumn: "Ocultar coluna",
          columnMenuUnsort: "Remover ordenação",
          columnMenuSortAsc: "Ordenar por ASC",
          columnMenuSortDesc: "Ordenar por DESC",
          columnMenuManagePivot: "Gerir pivot",
          paginationRowsPerPage: "Linhas por página:",
          paginationDisplayedRows: ({ from, to, count, estimated }) => {
            if (!estimated) {
              return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
            }
            const estimatedLabel =
              estimated && estimated > to
                ? `cerca de ${estimated}`
                : `mais de ${to}`;
            return `${from}–${to} de ${count !== -1 ? count : estimatedLabel}`;
          },
          noRowsLabel: "Sem encomendas",
          noResultsOverlayLabel: "Sem resultados",
          footerRowSelected: (count) => `${count} linha(s) selecionada(s)`,
        }}
        getCellClassName={(params) => {
          const fieldClasses: Record<string, string> = {
            id: "font-mono text-xs text-gray-500",
            idempotency_key: "font-mono text-xs",
            total_cents: "font-mono text-xs force-text-right",
            created_at: "text-xs text-gray-600",
            updated_at: "text-xs text-gray-600",
          };
          return params.row ? fieldClasses[params.field] || "" : "";
        }}
        sx={{ border: 0 }}
      />
    </Paper>
  );
};

export default ListOmsOrderItems;
